import {
  ClusterVersionBumpResponse,
  ClusterWipeResponse,
  CreateModpackConfigRequest,
  CreateModpackConfigResponse,
  DeleteModpackConfigResponse,
  DevSyncResponse,
  ModpackConfig,
  ModpackConfigClusterResponse,
  ModpackConfigEnv,
  ModpackConfigListResponse,
  ModpackConfigVocabularies,
  UpdateModpackConfigRequest,
  UpdateModpackConfigResponse,
  AnnounceLaunchResult,
  AnnounceUpdateResult,
  ChangeClusterHeadResponse,
  DiscordTargets,
  ProvisionResult,
} from '../types/modpackConfig';

/**
 * Modpack server configs.
 *
 * Goes through argus-backend rather than the public configs API at
 * api.playcdu.co/configs: that endpoint's only credential is a shared token
 * granting full read and write, which must never reach a browser. The backend
 * checks the signed-in staff member's permission, writes to D1 itself, and
 * records an audit entry.
 */
const MODPACK_CONFIGS_API_BASE_URL = 'https://spy.playcdu.co/api/notes/modpack-configs';

class ModpackConfigsApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${MODPACK_CONFIGS_API_BASE_URL}${endpoint}`, {
      ...options,
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const errorText = await response.text();
      // The backend answers { error } with a sentence meant for the person
      // reading it — a refused rename, a field that needs admin — so surface
      // that rather than burying it behind the status line.
      try {
        const parsed = JSON.parse(errorText);
        if (parsed?.error) throw new Error(parsed.error);
      } catch (e) {
        if (e instanceof Error && e.message && !e.message.startsWith('Unexpected')) throw e;
      }
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  private json(body: unknown, method: string): RequestInit {
    return {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    };
  }

  private envQuery(env: ModpackConfigEnv, extra?: Record<string, string>): string {
    const params = new URLSearchParams({ env, ...(extra ?? {}) });
    return `?${params.toString()}`;
  }

  async listConfigs(env: ModpackConfigEnv = 'production'): Promise<ModpackConfigListResponse> {
    return this.request<ModpackConfigListResponse>(this.envQuery(env));
  }

  async getConfig(address: string, env: ModpackConfigEnv = 'production'): Promise<ModpackConfig> {
    return this.request<ModpackConfig>(
      `/${encodeURIComponent(address)}${this.envQuery(env)}`,
    );
  }

  async getVocabularies(env: ModpackConfigEnv = 'production'): Promise<ModpackConfigVocabularies> {
    return this.request<ModpackConfigVocabularies>(`/vocabularies${this.envQuery(env)}`);
  }

  async getCluster(
    clusterParent: number,
    env: ModpackConfigEnv = 'production',
  ): Promise<ModpackConfigClusterResponse> {
    return this.request<ModpackConfigClusterResponse>(
      `/cluster/${clusterParent}${this.envQuery(env)}`,
    );
  }

  async createConfig(
    body: CreateModpackConfigRequest,
    env: ModpackConfigEnv = 'production',
  ): Promise<CreateModpackConfigResponse> {
    return this.request<CreateModpackConfigResponse>(this.envQuery(env), this.json(body, 'POST'));
  }

  /**
   * Send only what changed. Mr. Roboto rewrites popularity for every server
   * every 100 seconds, so a whole-row write would clobber it.
   */
  async updateConfig(
    address: string,
    patch: UpdateModpackConfigRequest,
    env: ModpackConfigEnv = 'production',
  ): Promise<UpdateModpackConfigResponse> {
    return this.request<UpdateModpackConfigResponse>(
      `/${encodeURIComponent(address)}${this.envQuery(env)}`,
      this.json(patch, 'PATCH'),
    );
  }

  async deleteConfig(
    address: string,
    env: ModpackConfigEnv = 'production',
  ): Promise<DeleteModpackConfigResponse> {
    return this.request<DeleteModpackConfigResponse>(
      `/${encodeURIComponent(address)}${this.envQuery(env)}`,
      { method: 'DELETE' },
    );
  }

  /** Set one version across every server in a cluster, reporting each one. */
  async bumpClusterVersion(
    clusterParent: number,
    modpackVersion: string,
    env: ModpackConfigEnv = 'production',
  ): Promise<ClusterVersionBumpResponse> {
    return this.request<ClusterVersionBumpResponse>(
      `/cluster/${clusterParent}/version${this.envQuery(env)}`,
      this.json({ modpack_version: modpackVersion }, 'POST'),
    );
  }

  /**
   * Record a world wipe on every server in a cluster. `dateWiped` is unix
   * seconds; leaving it out records the wipe as now.
   */
  async recordClusterWipe(
    clusterParent: number,
    dateWiped: number | undefined,
    env: ModpackConfigEnv = 'production',
  ): Promise<ClusterWipeResponse> {
    return this.request<ClusterWipeResponse>(
      `/cluster/${clusterParent}/wipe${this.envQuery(env)}`,
      this.json(dateWiped === undefined ? {} : { date_wiped: dateWiped }, 'POST'),
    );
  }

  // --- The Discord side ---------------------------------------------------
  //
  // Argus owns the config rows but only Mr. Roboto can touch Discord, so these
  // go through argus-backend to the bot, which runs the same code its slash
  // commands do.

  /** Categories a forum can be created in, and forums it can be attached to. */
  async getDiscordTargets(): Promise<DiscordTargets> {
    return this.request<DiscordTargets>('/discord-targets');
  }

  /** Create or attach the pack's forum and write its Discord link row. */
  async provision(
    address: string,
    target: { channel_id?: string; category_id?: string },
    env: ModpackConfigEnv = 'production',
  ): Promise<ProvisionResult> {
    return this.request<ProvisionResult>(
      `/${encodeURIComponent(address)}/provision${this.envQuery(env)}`,
      this.json(target, 'POST'),
    );
  }

  /**
   * Announce a completed cluster update.
   *
   * The backend refuses when any member is still on the old version — the
   * announcement pings everyone who plays the pack, so a half-updated cluster
   * must not be announced.
   */
  async announceClusterUpdate(
    clusterParent: number,
    body: { old_version: string; new_version: string; changelog_url?: string },
    env: ModpackConfigEnv = 'production',
  ): Promise<AnnounceUpdateResult> {
    return this.request<AnnounceUpdateResult>(
      `/cluster/${clusterParent}/announce${this.envQuery(env)}`,
      this.json(body, 'POST'),
    );
  }

  /**
   * Post the launch embed for a pack: one server (`location`), or several
   * (`servers`, each a config in this environment with its own location).
   */
  async announceLaunch(
    address: string,
    body: {
      description: string;
      location?: string;
      modpack_url?: string;
      modpack_version?: string;
      servers?: { server_address: string; location: string }[];
    },
    env: ModpackConfigEnv = 'production',
  ): Promise<AnnounceLaunchResult> {
    return this.request<AnnounceLaunchResult>(
      `/${encodeURIComponent(address)}/announce-launch${this.envQuery(env)}`,
      this.json(body, 'POST'),
    );
  }

  /**
   * Make a different member the head of its cluster. Every member is
   * re-parented and the Discord link row follows, so the role and forum stay
   * with the cluster.
   */
  async setClusterHead(
    clusterParent: number,
    serverAddress: string,
    env: ModpackConfigEnv = 'production',
  ): Promise<ChangeClusterHeadResponse> {
    return this.request<ChangeClusterHeadResponse>(
      `/cluster/${clusterParent}/head${this.envQuery(env)}`,
      this.json({ server_address: serverAddress }, 'POST'),
    );
  }

  /** Wipes the dev table and replaces it with a copy of production. */
  async syncDevFromProduction(): Promise<DevSyncResponse> {
    return this.request<DevSyncResponse>('/dev/sync', this.json({}, 'POST'));
  }
}

export const modpackConfigsApiService = new ModpackConfigsApiService();
